import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { posExportsApi, type PosExportDevice, type PosExportKind } from "../../api/posExportsApi";
import { useAuth } from "../../auth/AuthContext";
import { canAccessEasyRetailReports } from "../../auth/roles";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoIso(days: number) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

const EXPORT_CARDS: Array<{
  kind: PosExportKind;
  title: string;
  description: string;
  icon: string;
}> = [
  {
    kind: "sales",
    title: "Venduto",
    description: "Righe vendita (articoli, prezzi, pagamenti) da scontrini sincronizzati in Corevian.",
    icon: "bi-cart-check",
  },
  {
    kind: "receipts",
    title: "Scontrini",
    description: "Documenti fiscali stampati in cassa (progressivo, totale, cliente).",
    icon: "bi-receipt",
  },
  {
    kind: "invoices",
    title: "Fatture",
    description: "Fatture elettroniche collegate alle vendite POS / Easy Retail.",
    icon: "bi-file-earmark-text",
  },
  {
    kind: "non_fiscal",
    title: "Preventivi / non fiscali",
    description: "Documenti non fiscali stampati in cassa (preventivi, copie non fiscali).",
    icon: "bi-file-earmark",
  },
];

function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function PosDownloadsPage() {
  const { token, user } = useAuth();
  const canView = canAccessEasyRetailReports(user?.role);
  const [from, setFrom] = useState(() => daysAgoIso(7));
  const [to, setTo] = useState(() => todayIso());
  const [posCode, setPosCode] = useState("");
  const [devices, setDevices] = useState<PosExportDevice[]>([]);
  const [busyKind, setBusyKind] = useState<PosExportKind | "">("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [previews, setPreviews] = useState<Partial<Record<PosExportKind, number>>>({});

  const filters = useMemo(
    () => ({ from, to, posCode: posCode.trim() || undefined }),
    [from, to, posCode],
  );

  const loadDevices = useCallback(async () => {
    if (!token) return;
    try {
      const res = await posExportsApi.listDevices(token);
      setDevices(res.items || []);
    } catch (e: any) {
      setError(e?.message || "Impossibile caricare le casse");
    }
  }, [token]);

  useEffect(() => {
    void loadDevices();
  }, [loadDevices]);

  async function handlePreview(kind: PosExportKind) {
    if (!token) return;
    setBusyKind(kind);
    setError("");
    setMessage("");
    try {
      const res = await posExportsApi.preview(token, { kind, ...filters });
      setPreviews((prev) => ({ ...prev, [kind]: res.rows }));
      setMessage(`${res.filename}: ${res.rows} righe nel periodo selezionato`);
    } catch (e: any) {
      setError(e?.message || "Anteprima fallita");
    } finally {
      setBusyKind("");
    }
  }

  async function handleDownload(kind: PosExportKind) {
    if (!token) return;
    setBusyKind(kind);
    setError("");
    setMessage("");
    try {
      const file = await posExportsApi.download(token, { kind, ...filters });
      triggerBrowserDownload(file.blob, file.filename);
      setPreviews((prev) => ({ ...prev, [kind]: file.rows }));
      setMessage(`Scaricato ${file.filename} (${file.rows} righe)`);
    } catch (e: any) {
      setError(e?.message || "Download fallito");
    } finally {
      setBusyKind("");
    }
  }

  if (!canView) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-warning mb-0">Non hai i permessi per scaricare i report POS.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h1 className="h4 mb-1">Scarica venduto e documenti</h1>
          <p className="text-muted small mb-0">
            Esporta CSV da Corevian: venduto, scontrini, fatture e documenti/preventivi non fiscali.
          </p>
        </div>
        <Link to="/reports" className="btn btn-sm btn-outline-secondary">
          Torna ai report
        </Link>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-6 col-md-3">
              <label className="form-label small mb-1">Dal</label>
              <input type="date" className="form-control form-control-sm" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small mb-1">Al</label>
              <input type="date" className="form-control form-control-sm" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label small mb-1">Cassa (opzionale)</label>
              <select className="form-select form-select-sm" value={posCode} onChange={(e) => setPosCode(e.target.value)}>
                <option value="">Tutte le casse</option>
                {devices.map((d) => (
                  <option key={d.id} value={d.code}>
                    {d.code}
                    {d.name ? ` — ${d.name}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary w-100"
                onClick={() => {
                  setFrom(daysAgoIso(30));
                  setTo(todayIso());
                }}
              >
                Ultimi 30 gg
              </button>
            </div>
          </div>
        </div>
      </div>

      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      {message ? <div className="alert alert-success py-2">{message}</div> : null}

      <div className="row g-3">
        {EXPORT_CARDS.map((card) => {
          const busy = busyKind === card.kind;
          const rows = previews[card.kind];
          return (
            <div key={card.kind} className="col-12 col-md-6">
              <div className="card shadow-sm h-100">
                <div className="card-body d-flex flex-column">
                  <div className="d-flex align-items-start gap-3 mb-3">
                    <div className="rounded-3 bg-light p-2">
                      <i className={`bi ${card.icon} fs-4 text-primary`} />
                    </div>
                    <div>
                      <h2 className="h6 mb-1">{card.title}</h2>
                      <p className="small text-muted mb-0">{card.description}</p>
                      {typeof rows === "number" ? (
                        <div className="small mt-2">
                          Righe periodo: <strong>{rows}</strong>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="mt-auto d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      disabled={busy || !from || !to}
                      onClick={() => void handlePreview(card.kind)}
                    >
                      {busy ? "..." : "Anteprima"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      disabled={busy || !from || !to}
                      onClick={() => void handleDownload(card.kind)}
                    >
                      {busy ? "Download..." : "Scarica CSV"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
